import {
  Clear as ClearIcon,
  DeleteRounded as DeleteRoundedIcon,
  DownloadRounded as DownloadRoundedIcon,
  EditRounded as EditRoundedIcon,
  RestartAltRounded as RestartAltRoundedIcon,
  ScheduleRounded as ScheduleRoundedIcon,
  SendRounded as SendRoundedIcon
} from '@mui/icons-material'
import { Box, ButtonGroup, CircularProgress, IconButton, Stack } from '@mui/material'
import { t } from 'i18next'
import moment from 'moment'
import { useState } from 'react'

import Can from '../../Layout/Can/Can.jsx'
import Show from '../../Layout/Can/Show.jsx'
import { useAuth } from '../../Shared/Contexts/AuthContext.jsx'
import { useGlobalContext } from '../../Shared/Contexts/GlobalContext.jsx'
import { useDevices } from '../Contexts/DevicesContext.jsx'

const ActionFooter = ({ device, actionsDisabled = false, onDelete, onHardDelete, onRestore, onEdit, onAction }) => {
  const { currentUser: { isSuperAdmin } } = useAuth()
  const isDisabled = device.status === 'disabled'
  const { useDeviceMeasurementsDownload } = useDevices()
  const { openErrorAlert } = useGlobalContext()
  const [downloadInProgress, setDownloadInProgress] = useState(false)

  const handleActions = (device) => onAction(device)

  const handleEdit = (device) => onEdit(device)

  const handleDownload = (id, name, serial, from, to) => {
    setDownloadInProgress(true)
    useDeviceMeasurementsDownload({
      id,
      name,
      serial,
      from,
      to
    })
      .catch(() => openErrorAlert({ keys: ['download'] }))
      .finally(() => setDownloadInProgress(false))
  }

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        px={1}>
        <Stack
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          direction="row"
          spacing={1}>
          <ScheduleRoundedIcon fontSize="small" />{' '}
          <small>
            {t('devices.lastUpdate')}{' '}
            {device.lastMeasurementAt ? moment(device.lastMeasurementAt).format('DD.MM.YYYY HH:mm:ss') : t('common.noPresent')}
          </small>
        </Stack>
        <Box>
          <ButtonGroup size="small">
            {!isDisabled && (
              <Can action="devices_d">
                <IconButton
                  key="delete"
                  onClick={onDelete}
                  aria-label={`${t('devices.delete')}`}
                  color="error">
                  <DeleteRoundedIcon fontSize="small" />
                </IconButton>
              </Can>
            )}
            {isDisabled && isSuperAdmin && (
              <IconButton
                key="restore"
                onClick={() => onRestore(device)}
                aria-label={`${t('devices.restore')} ${device.name}`}
                color="success">
                <RestartAltRoundedIcon fontSize="small" />
              </IconButton>
            )}
            {isSuperAdmin && (
              <IconButton
                key="hard-delete"
                onClick={onHardDelete}
                aria-label={`${t('devices.hardDelete')} ${device.name}`}
                color="error">
                <ClearIcon fontSize="small" />
              </IconButton>
            )}

            {!isDisabled && (
              <Can action="devices_u">
                <IconButton
                  key="actions"
                  disabled={actionsDisabled}
                  onClick={() => handleActions(device)}
                  aria-label={`${t('devices.actionsDevice.title')}`}
                  color="primary">
                  <SendRoundedIcon fontSize="small" />
                </IconButton>
              </Can>
            )}
            <Can action="devices_r">
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center">
                <Show when={() => downloadInProgress}>
                  <CircularProgress size={20} />
                </Show>
                <Show when={() => !downloadInProgress}>
                  <IconButton
                    variant="text"
                    key="download"
                    color="primary"
                    aria-label={`${t('devices.exportStatistics')}`}
                    onClick={() =>
                      handleDownload(device.id, device.name, device.serial, moment().subtract(5, 'days').toISOString(), moment().toISOString())
                    }>
                    <DownloadRoundedIcon fontSize="small" />
                  </IconButton>
                </Show>
              </Box>
            </Can>
            {!isDisabled && (
              <Can action="devices_u">
                <IconButton
                  key="edit"
                  aria-label={`${t('devices.update')}`}
                  color="primary"
                  onClick={() => handleEdit(device)}>
                  <EditRoundedIcon fontSize="small" />
                </IconButton>
              </Can>
            )}
          </ButtonGroup>
        </Box>
      </Box>
    </>
  )
}

export default ActionFooter
